import { ChangeEvent, ComponentProps, KeyboardEvent, useEffect, useId, useRef, useState } from 'react'

import { SendRightIcon } from '../../../icons'
import { SizeOptions } from '../../../types/types'
import { AstraButton } from '../AstraButton/AstraButton'
import { AstraInputWrapper, getHelperTextId, InputBase, inputTypographyStyles } from '../AstraInputWrapper/AstraInputWrapper'
import { ASTRA_PRIVACY_BLUR_REVEAL_CLASS, useAstraPrivacyMode } from '../AstraPrivacyMode/AstraPrivacyMode'

type Props = {
    defaultValue?: string
    onEnterPressed?: (value: string) => void
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
    onClick?: (value: string) => void
    size?: SizeOptions
    isValid?: (value: string) => boolean
    onSubmit?: (value: string) => void
} & InputBase &
    Omit<ComponentProps<'textarea'>, 'defaultValue' | 'onClick'>

const sizeToNumber = {
    small: 20, // 1.25rem
    medium: 28, // 1.75rem
    large: 36, // 2.25rem
}

export const AstraTextArea = ({
    defaultValue,
    helperText,
    errorText,
    onEnterPressed,
    onClick,
    onChange,
    isValid,
    size = 'medium',
    label,
    disabled,
    readOnly,
    error = false,
    fullWidth = false,
    startIcon,
    endIcon,
    rows,
    borderless = false,
    name,
    placeholder,
    ...rest
}: Props) => {
    const id = useId()
    const [value, setValue] = useState<string | undefined>(defaultValue)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const isPrivacyMode = useAstraPrivacyMode()

    const resetStateOnChange = () => {
        setValue(defaultValue)
    }

    useEffect(resetStateOnChange, [defaultValue])

    const expandTextArea = () => {
        if (textAreaRef.current) {
            const mappedSize = sizeToNumber[size]
            const RESET_SIZE = '0px'
            const PADDING = 8
            const maxCollapsedSize = mappedSize + PADDING
            if (rows) {
                textAreaRef.current.style.height = `${rows * mappedSize}px`
                return
            }
            // We need to reset the height momentarily to get the correct scrollHeight for the textarea
            textAreaRef.current.style.height = RESET_SIZE
            const scrollHeight = textAreaRef.current.scrollHeight

            // We then set the height directly, outside of the render loop
            // Trying to set this with state or a ref will product an incorrect value.
            textAreaRef.current.style.height = scrollHeight + 4 + 'px'
            if (scrollHeight > maxCollapsedSize) {
                setIsExpanded(true)
                textAreaRef.current.style.paddingTop = '0.25rem' // Adjust padding to match the input size
            } else {
                setIsExpanded(false)
                textAreaRef.current.style.paddingTop = RESET_SIZE // Default padding for small size
                textAreaRef.current.style.paddingBottom = RESET_SIZE // Default padding for small size
            }
        }
    }

    useEffect(expandTextArea, [textAreaRef, value, size, rows, rest.value])

    const handleKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            const trimmed = event.currentTarget.value.trim()
            if (onEnterPressed && (!isValid || isValid(trimmed))) {
                onEnterPressed(trimmed)
                setValue('')
            }
        }
        event.stopPropagation()
    }

    const handleButtonClick = () => {
        value && isValid?.(value) && onClick?.(value)
        setValue('')
    }

    return (
        <AstraInputWrapper
            id={id}
            label={label}
            labelHtmlFor={name}
            helperText={helperText}
            errorText={errorText}
            error={error}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth={fullWidth}
            size={size}
            startIcon={startIcon}
            endIcon={endIcon}
            className={isExpanded ? 'is-expanded' : ''}
            onClick={() => textAreaRef.current?.focus()}
            borderless={borderless}
        >
            <textarea
                ref={textAreaRef}
                name={name}
                disabled={disabled}
                readOnly={readOnly}
                aria-label={name}
                aria-describedby={helperText ? getHelperTextId(id) : undefined}
                className={`w-full resize-none self-center overflow-y-hidden bg-transparent outline-none ${inputTypographyStyles[size]} ${isPrivacyMode ? ASTRA_PRIVACY_BLUR_REVEAL_CLASS : ''}`}
                onChange={e => {
                    setValue(e.target.value)
                    onChange && onChange(e)
                }}
                value={value}
                placeholder={placeholder}
                onKeyDown={handleKeyPress}
                {...rest}
            />

            {onClick && (
                <div
                    className={`group-[.is-expanded]:mb-[.25rem] group-[.is-expanded]:mr-[-.25rem] group-[.is-expanded]:self-end ${size === 'small' ? 'mr-[-.5rem]' : 'mr-[-.25rem]'}`}
                >
                    <AstraButton
                        disabled={!value || value.trim().length < 1 || disabled || readOnly}
                        icon={<SendRightIcon />}
                        size="small"
                        onClick={handleButtonClick}
                    />
                </div>
            )}
        </AstraInputWrapper>
    )
}
